/**
 * Custom React Hook for Patient Diagnosis Management
 * Handles state, auto-save, and API interactions
 *
 * @author CareX Frontend Team
 */

import { useState, useEffect, useCallback } from 'react';
import patientDiagnosisService from '../services/patientDiagnosisService';
import {
  PatientDiagnosisDTO,
  ExamVitalsDTO,
  AmbulatoryStatusDTO,
  DiagnosesTreatmentsDTO,
  PhysicalHealthAssessmentDTO,
  MentalConditionAssessmentDTO,
  CapacityMedicationAssessmentDTO,
  HospicePhysicianInfoDTO,
} from '../types/diagnosis.types';

interface UsePatientDiagnosisProps {
  residentId: string | null;
}

interface UsePatientDiagnosisReturn {
  // State
  diagnosis: PatientDiagnosisDTO | null;
  loading: boolean;
  saving: boolean;
  error: string | null;

  // Actions
  saveDraft: () => Promise<void>;
  updateExamVitals: (data: ExamVitalsDTO) => Promise<void>;
  updateAmbulatoryStatus: (data: AmbulatoryStatusDTO) => Promise<void>;
  updateDiagnosesTreatments: (data: DiagnosesTreatmentsDTO) => Promise<void>;
  updatePhysicalHealthAssessment: (data: PhysicalHealthAssessmentDTO) => Promise<void>;
  updateMentalConditionAssessment: (data: MentalConditionAssessmentDTO) => Promise<void>;
  updateCapacityMedicationAssessment: (data: CapacityMedicationAssessmentDTO) => Promise<void>;
  updateHospicePhysicianInfo: (data: HospicePhysicianInfoDTO) => Promise<void>;
  submitDiagnosis: () => Promise<void>;
  refreshDiagnosis: () => Promise<void>;

  // Helpers
  isEditable: boolean;
  completionPercentage: number;
}

/**
 * Custom hook for managing patient diagnosis
 * Provides auto-save, state management, and error handling
 */
export const usePatientDiagnosis = ({
  residentId,
}: UsePatientDiagnosisProps): UsePatientDiagnosisReturn => {
  const [diagnosis, setDiagnosis] = useState<PatientDiagnosisDTO | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize or fetch diagnosis
  useEffect(() => {
    if (!residentId) {
      setLoading(false);
      return;
    }

    const fetchDiagnosis = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get or create draft diagnosis
        const fetchedDiagnosis = await patientDiagnosisService.getOrCreateDraft(residentId);
        setDiagnosis(fetchedDiagnosis);
      } catch (err: any) {
        console.error('Error fetching diagnosis:', err);
        setError(err.response?.data?.message || 'Failed to load diagnosis');
      } finally {
        setLoading(false);
      }
    };

    fetchDiagnosis();
  }, [residentId]);

  // Refresh diagnosis from server
  const refreshDiagnosis = useCallback(async () => {
    if (!diagnosis?.id) return;

    try {
      const updated = await patientDiagnosisService.getDiagnosisById(diagnosis.id);
      setDiagnosis(updated);
    } catch (err: any) {
      console.error('Error refreshing diagnosis:', err);
      setError(err.response?.data?.message || 'Failed to refresh diagnosis');
    }
  }, [diagnosis?.id]);

  // Save draft (create new if doesn't exist)
  const saveDraft = useCallback(async () => {
    if (!residentId) return;

    try {
      setSaving(true);
      setError(null);

      if (!diagnosis?.id) {
        // Create new draft
        const newDiagnosis = await patientDiagnosisService.createDiagnosis({
          residentId,
          diagnosisDate: new Date().toISOString().split('T')[0],
        });
        setDiagnosis(newDiagnosis);
      }
    } catch (err: any) {
      console.error('Error saving draft:', err);
      setError(err.response?.data?.message || 'Failed to save draft');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [residentId, diagnosis?.id]);

  // Tab update methods
  const updateExamVitals = useCallback(
    async (data: ExamVitalsDTO) => {
      if (!diagnosis?.id) {
        throw new Error('Diagnosis not initialized');
      }

      try {
        setSaving(true);
        setError(null);

        const updated = await patientDiagnosisService.updateExamVitals(diagnosis.id, data);
        setDiagnosis(updated);
      } catch (err: any) {
        console.error('Error updating exam vitals:', err);
        setError(err.response?.data?.message || 'Failed to save exam vitals');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [diagnosis?.id]
  );

  const updateAmbulatoryStatus = useCallback(
    async (data: AmbulatoryStatusDTO) => {
      if (!diagnosis?.id) throw new Error('Diagnosis not initialized');

      try {
        setSaving(true);
        setError(null);

        const updated = await patientDiagnosisService.updateAmbulatoryStatus(diagnosis.id, data);
        setDiagnosis(updated);
      } catch (err: any) {
        console.error('Error updating ambulatory status:', err);
        setError(err.response?.data?.message || 'Failed to save ambulatory status');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [diagnosis?.id]
  );

  const updateDiagnosesTreatments = useCallback(
    async (data: DiagnosesTreatmentsDTO) => {
      if (!diagnosis?.id) throw new Error('Diagnosis not initialized');

      try {
        setSaving(true);
        setError(null);

        const updated = await patientDiagnosisService.updateDiagnosesTreatments(diagnosis.id, data);
        setDiagnosis(updated);
      } catch (err: any) {
        console.error('Error updating diagnoses/treatments:', err);
        setError(err.response?.data?.message || 'Failed to save diagnoses and treatments');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [diagnosis?.id]
  );

  const updatePhysicalHealthAssessment = useCallback(
    async (data: PhysicalHealthAssessmentDTO) => {
      if (!diagnosis?.id) throw new Error('Diagnosis not initialized');

      try {
        setSaving(true);
        setError(null);

        const updated = await patientDiagnosisService.updatePhysicalHealthAssessment(diagnosis.id, data);
        setDiagnosis(updated);
      } catch (err: any) {
        console.error('Error updating physical health:', err);
        setError(err.response?.data?.message || 'Failed to save physical health assessment');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [diagnosis?.id]
  );

  const updateMentalConditionAssessment = useCallback(
    async (data: MentalConditionAssessmentDTO) => {
      if (!diagnosis?.id) throw new Error('Diagnosis not initialized');

      try {
        setSaving(true);
        setError(null);

        const updated = await patientDiagnosisService.updateMentalConditionAssessment(diagnosis.id, data);
        setDiagnosis(updated);
      } catch (err: any) {
        console.error('Error updating mental condition:', err);
        setError(err.response?.data?.message || 'Failed to save mental condition assessment');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [diagnosis?.id]
  );

  const updateCapacityMedicationAssessment = useCallback(
    async (data: CapacityMedicationAssessmentDTO) => {
      if (!diagnosis?.id) throw new Error('Diagnosis not initialized');

      try {
        setSaving(true);
        setError(null);

        const updated = await patientDiagnosisService.updateCapacityMedicationAssessment(diagnosis.id, data);
        setDiagnosis(updated);
      } catch (err: any) {
        console.error('Error updating capacity/medication:', err);
        setError(err.response?.data?.message || 'Failed to save capacity and medication assessment');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [diagnosis?.id]
  );

  const updateHospicePhysicianInfo = useCallback(
    async (data: HospicePhysicianInfoDTO) => {
      if (!diagnosis?.id) throw new Error('Diagnosis not initialized');

      try {
        setSaving(true);
        setError(null);

        const updated = await patientDiagnosisService.updateHospicePhysicianInfo(diagnosis.id, data);
        setDiagnosis(updated);
      } catch (err: any) {
        console.error('Error updating hospice/physician info:', err);
        setError(err.response?.data?.message || 'Failed to save hospice and physician information');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [diagnosis?.id]
  );

  // Submit diagnosis for review
  const submitDiagnosis = useCallback(async () => {
    if (!diagnosis?.id) throw new Error('Diagnosis not initialized');

    try {
      setSaving(true);
      setError(null);

      const updated = await patientDiagnosisService.submitDiagnosis(diagnosis.id);
      setDiagnosis(updated);
    } catch (err: any) {
      console.error('Error submitting diagnosis:', err);
      setError(err.response?.data?.message || 'Failed to submit diagnosis');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [diagnosis?.id]);

  // Computed properties
  const isEditable = diagnosis ? patientDiagnosisService.isEditable(diagnosis) : false;
  const completionPercentage = diagnosis?.completionPercentage || 0;

  return {
    // State
    diagnosis,
    loading,
    saving,
    error,

    // Actions
    saveDraft,
    updateExamVitals,
    updateAmbulatoryStatus,
    updateDiagnosesTreatments,
    updatePhysicalHealthAssessment,
    updateMentalConditionAssessment,
    updateCapacityMedicationAssessment,
    updateHospicePhysicianInfo,
    submitDiagnosis,
    refreshDiagnosis,

    // Helpers
    isEditable,
    completionPercentage,
  };
};
